'use client'

import type { IMarker, IResponseGetRoute } from '@repo/sdk'

import { useJsApiLoader } from '@react-google-maps/api'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

interface IMapContext {
  estimatedTrip?: number
  fitBounds: (markers: IMarker[], map: google.maps.Map) => void
  getRoute: (routes: string[]) => Promise<IResponseGetRoute | void>
  map?: google.maps.Map
  markerList?: IMarker[]
  onLoad: (map: google.maps.Map) => void
  onUnmount: () => void
  route?: string[]
  setEstimatedTrip: (miles: number) => void
  setMarkerList: (markers: IMarker[]) => void
  setRoute: (newRoute: string[]) => void
  showEstimatedTrip: boolean
}

export const MapContext = createContext<IMapContext>({} as IMapContext)

interface ProviderProps {
  children: ReactNode
  className?: string
  getRoute: (routes: string[]) => Promise<IResponseGetRoute | void>
  isLoading?: boolean
  setIsLoading?: Dispatch<SetStateAction<boolean>>
  showEstimatedTrip?: boolean
}

const center = {
  lat: '54',
  lng: '-2',
  zoom: 1,
}

export const MapContextProvider = ({
  children,
  className,
  getRoute,
  isLoading = false,
  setIsLoading,
  showEstimatedTrip = false,
}: ProviderProps) => {
  const [estimatedTrip, setEstimatedTrip] = useState<number | undefined>(undefined)
  const [markerList, setMarkerList] = useState<IMarker[]>()
  const [route, setRoute] = useState<string[]>()
  const [map, setMap] = useState<google.maps.Map>()

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGlE_API_KEY!,
    id: 'google-map-script',
  })

  const fitBounds = (markers: IMarker[], map: google.maps.Map) => {
    if (markers.length === 1) {
      map.setCenter(new google.maps.LatLng(parseFloat(markers[0].lat), parseFloat(markers[0].lng)))
      setMap(map)
      return
    }

    const bounds = new window.google.maps.LatLngBounds()
    markers.forEach(marker => {
      bounds.extend(new google.maps.LatLng(parseFloat(marker.lat), parseFloat(marker.lng)))
    })

    map.fitBounds(bounds)
    setMap(map)
  }

  const onLoad = useCallback((map: google.maps.Map) => {
    if (isLoading) {
      fitBounds([center], map)
      setIsLoading?.(false)
    }
  }, [])

  const onUnmount = useCallback(() => {
    setMap(undefined)
  }, [])

  const value = useMemo(
    () => ({
      estimatedTrip,
      fitBounds,
      getRoute,
      map,
      markerList,
      onLoad,
      onUnmount,
      route,
      setEstimatedTrip,
      setMarkerList,
      setRoute,
      showEstimatedTrip,
    }),
    [estimatedTrip, isLoading, markerList, route, map, showEstimatedTrip],
  )

  return useMemo(
    () => (
      <MapContext.Provider value={value}>
        {isLoaded ? children : <div className={className} />}
      </MapContext.Provider>
    ),
    [isLoaded, value],
  )
}

export const useMapContext = () => {
  const context = useContext(MapContext)

  if (!context) {
    throw new Error('useMapContext must be used within a MapContextProvider')
  }

  return context
}
